import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import getConfig from 'next/config';
import get from 'lodash.get';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import { shareImage } from 'lib/social';
import { getCanonical } from 'lib/urlUtils';
import getISODate from 'lib/getISODate';
import getSocialImage from 'lib/getSocialImage';
import { isNewsVertical, isTelemundoVertical } from 'lib/vertical';

export const constructJSONLD = ({
  url = '',
  section = '',
  pageType = '',
  vertical = '',
  id = '',
}) => JSON.stringify({
  '@context': {
    '@vocab': 'http://schema.org',
    pageType: {
      '@id': 'Text',
      '@type': '@id',
    },
    vertical: {
      '@id': 'Text',
      '@type': '@id',
    },
    subVertical: {
      '@id': 'Text',
      '@type': '@id',
    },
    section: {
      '@id': 'Text',
      '@type': '@id',
    },
    subSection: {
      '@id': 'Text',
      '@type': '@id',
    },
    packageId: {
      '@id': 'Text',
      '@type': '@id',
    },
    sponsor: {
      '@id': 'Text',
      '@type': '@id',
    },
    appVersion: {
      '@id': 'Text',
      '@type': '@id',
    },
  },
  '@type': 'Dataset',
  name: 'additionalTaxonomy',
  description: 'This is additional taxonomy that helps us with analytics',
  url,
  pageType,
  vertical,
  subVertical: section || '', // subvertical is always the section on the front (but not in content)
  section: '',
  subSection: '',
  packageId: id,
  sponsor: '', // no sponsored fronts I think
  appVersion: get(getConfig(), ['publicRuntimeConfig', 'appVersion'], ''),
});

function FrontMetadata({
  vertical, curation, section, domain, firstContentInZoneA,
}) {
  const {
    dateCreated,
    dateModified,
    id,
    pageType,
    seoDescription,
    seoTitle,
    socialImage,
    title,
  } = curation;

  let { seoTitle: titleValue } = curation;
  let twitterSite;
  let organization;

  if (vertical === 'msnbc') {
    organization = 'MSNBC';
    twitterSite = 'MSNBC';
  } else if (vertical === 'today') {
    organization = 'TODAY';
    titleValue += ` | ${organization}`;
    twitterSite = 'TodayShow';
  } else if (isTelemundoVertical(vertical)) {
    organization = 'Telemundo';
    titleValue += ` | ${organization}`;
    twitterSite = 'Telemundo';
  } else if (isNewsVertical(vertical)) {
    organization = 'NBC News';
    titleValue += ` | ${organization}`;
    twitterSite = 'NBCNews';
  }

  let url = `https://www.${domain}`;
  if (['news', 'msnbc', 'today', 'sponsoredcontent'].includes(vertical) === false) {
    url += `/${vertical}`;
  }
  if (section) {
    url += `/${section}`;
  }

  const image = getSocialImage(socialImage);

  const isShowLead = firstContentInZoneA && firstContentInZoneA.type === 'showLead' && firstContentInZoneA.metadata;
  const showDescription = isShowLead ? firstContentInZoneA.metadata.description : null;

  const jsonLdPageType = (() => {
    if (pageType === 'showPage') return pageType;

    return section ? 'SectionFront' : 'Front'; // need lowercase later, and try to not be hardcoded
  })();

  return (
    <Head>
      <title>{titleValue}</title>
      <meta name="description" content={seoDescription} />
      {vertical === 'news' && !section && (
        <meta name="pocket-site-verification" content="7d430daabab89c549b078097430be5" />
      )}
      {vertical === 'news' && !section && (
        <meta name="google-site-verification" content="xcFg25oVUcM7Zmt3RO2DdNXUrzie1AqgHOoKA_uTeks" />
      )}
      {vertical === 'today' && !section && (
        <meta name="google-site-verification" content="CW4UdAimFXD75AQd4jFiVmY4R-gOduNQJHSJMfzr8cw" />
      )}
      {vertical === 'msnbc' && !section && (
        <meta name="google-site-verification" content="REPjLi_yDHx7TLKgH3OC5uFnLfGp1M-1m8QUec6g8qQ" />
      )}

      <link rel="canonical" href={getCanonical(url)} />

      <meta property="og:url" content={url} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={shareImage(image.url, vertical, 'og')} />
      <meta property="og:image:secure_url" content={shareImage(image.url, vertical, 'og')} />
      <meta property="og:image:type" content={image.encodingFormat || 'image/gif'} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:image:src" content={shareImage(image.url, vertical, 'twitter')} />
      <meta name="twitter:title" content={seoTitle || title} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={shareImage(image.url, vertical, 'twitter')} />
      <meta name="thumbnail" content={shareImage(image.url, vertical)} />
      <script
        type="application/ld+json"
        data-test="front-jsonld"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: constructJSONLD({
            url, section, vertical, id, pageType: jsonLdPageType,
          }),
        }}
      />
      {pageType !== 'showPage' ? (
        <script
          type="application/ld+json"
          data-test="jsonld__WebSite"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: JSON.stringify(
              {
                '@context': 'http://schema.org',
                '@type': 'WebSite',
                url,
                identifier: { // not sure this should be included
                  '@type': 'PropertyValue',
                  propertyID: 'Document Id',
                  value: id,
                },
                headline: title,
                alternativeHeadline: seoTitle,
                dateCreated: getISODate(dateCreated),
                datePublished: getISODate(dateCreated),
                dateModified: getISODate(dateModified),
                potentialAction: {
                  '@type': 'SearchAction',
                  target: `https://www.${domain}/search?q={search_term_string}`,
                  'query-input': 'required name=search_term_string',
                },
                image: image.url ? {
                  '@type': 'ImageObject',
                  url: image.url,
                  width: image.width,
                  height: image.height,
                } : '',
                publisher: {
                  '@type': 'Organization',
                  name: organization,
                  logo: {
                    '@type': 'ImageObject',
                    url: 'https://nodeassets.nbcnews.com/cdnassets/projects/site-images/nbcnews-logo-white.png',
                    width: 166,
                    height: 24,
                  },
                },
                description: seoDescription,
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  inLanguage: 'English',
                  datePublished: dateCreated,
                  name: domain,
                  '@id': url,
                },
              },
            ),
          }}
        />
      ) : (
        <script
          type="application/ld+json"
          data-test="jsonld__TVSeries"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: JSON.stringify(
              {
                '@context': 'http://schema.org',
                '@type': 'TVSeries',
                url,
                productionCompany: vertical,
                temporalCoverage: showDescription,
                description: seoDescription,
                thumbnailUrl: shareImage(image.url, vertical),
              },
            ),
          }}
        />
      )}
    </Head>
  );
}

FrontMetadata.propTypes = {
  curation: CustomPropTypes.curation.isRequired,
  firstContentInZoneA: CustomPropTypes.package.isRequired,
  vertical: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
};


export default FrontMetadata;
